<template>
  <div>
    <v-row class="ma-0">
      <v-col cols="12" sm="6" class="d-flex align-center">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              @click="callBackRoute"
              v-bind="attrs"
              v-on="on"
              class="mr-3"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
          </template>
          <span>Back</span>
        </v-tooltip>
        <span class="text-h5"
          >Sub Hire {{ subHireFormDetails.sub_hire_id }}</span
        >
      </v-col>

      <v-col cols="12" class="heading pa-4">
        <v-row class="ma-0" align="center">
          <v-col cols="12" sm="auto" class="pa-0">
            <span class="text-subtitle-2 font-weight-bold text--secondary"
              >SUB HIRE SUMMARY</span
            >
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto" class="pa-0">
            <v-btn
              v-if="subHireFormDetails.status === 'Created'"
              color="success"
              depressed
              small
              class="mr-2"
              :disabled="!subHireFormDetails.transporter"
              @click="showMoveToPendingConfirm = true"
            >
              Move To Pending
            </v-btn>
            <v-btn
              color="primary"
              depressed
              small
              @click="showEditChargesDialog = true"
            >
              <v-icon left small>mdi-pencil</v-icon>
              {{ canEditCharges ? "Edit Charges" : "View Charges" }}
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="!subHireFormDetails.transporter"
          type="warning"
          dense
          text
          class="mt-3 mb-0"
        >
          Transporter is not assigned for this job. Add Leg, Remove Leg and Manage Charges are disabled until a transporter is set via Edit Charges.
        </v-alert>

        <v-divider class="my-3"></v-divider>

        <v-row class="ma-0" dense>
          <v-col
            cols="6"
            sm="4"
            md="2"
            v-for="field in summaryFields"
            :key="field.label"
          >
            <span class="text-caption text--secondary">{{ field.label }}</span>
            <p v-if="field.chip" class="mb-0">
              <v-chip
                label
                small
                :color="subHireStatusLighten(field.value)"
                :text-color="subHireStatusDarken(field.value)"
                class="font-weight-bold"
              >
                {{ field.value }}
              </v-chip>
            </p>
            <p v-else class="text-subtitle-1 font-weight-medium mb-0">
              {{ field.value }}
            </p>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <v-tabs v-model="activeTab">
          <v-tab>
            Legs
            <v-chip x-small color="primary" text-color="white" class="ml-2">
              {{ selectedLegs.length }}
            </v-chip>
          </v-tab>
          <v-tab>Charges</v-tab>
          <v-tab>Logs</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="mt-3">
          <!-- Legs tab -->
          <v-tab-item>
            <v-row class="ma-0 mb-2" align="center">
              <v-col cols="12" sm="4" class="Form-Columns-Dense">
                <v-text-field
                  v-model="legSearch"
                  label="Search"
                  outlined
                  dense
                  append-icon="mdi-magnify"
                  clearable
                  hide-details
                />
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="auto" class="Form-Columns-Dense">
                <Pagination
                  :pageNo="legPageNo"
                  :totalItems="legTotalItems"
                  :pageSize="legItemsPerPage"
                  :itemsPerPageValues="[10, 20, 50, 100]"
                  @itemsPerPageChange="legItemsPerPageChanged"
                  @prevPage="legPrevPage"
                  @nextPage="legNextPage"
                />
              </v-col>
              <v-col cols="12" sm="auto" class="Form-Columns-Dense">
                <v-btn
                  v-if="canEditLegs"
                  color="primary"
                  depressed
                  small
                  :disabled="!subHireFormDetails.transporter"
                  @click="showAddLegDialog = true"
                >
                  <v-icon left small>mdi-plus</v-icon> Add Leg
                </v-btn>
              </v-col>
            </v-row>

            <AgGridVue
              style="width: 100%; height: calc(100vh - 380px)"
              class="ag-theme-alpine"
              id="selectedLegGrid"
              :columnDefs="selectedLegHeaders"
              :context="context"
              @grid-ready="onSelectedLegGridReady"
              :defaultColDef="defaultColDef"
              :grid-options="selectedLegGridOptions"
              :rowData="displayedLegs"
            ></AgGridVue>
          </v-tab-item>

          <!-- Charges tab -->
          <v-tab-item>
            <v-row class="ma-0 mb-2" align="center">
              <v-col cols="12" sm="4" class="Form-Columns-Dense">
                <v-text-field
                  v-model="chargeSearch"
                  label="Search"
                  outlined
                  dense
                  append-icon="mdi-magnify"
                  clearable
                  hide-details
                />
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="auto" class="Form-Columns-Dense">
                <v-btn
                  color="primary"
                  depressed
                  small
                  :disabled="!subHireFormDetails.transporter"
                  @click="showManageChargesDialog = true"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  {{ canEditCharges ? "Manage Charges" : "View Charges" }}
                </v-btn>
              </v-col>
            </v-row>

            <AgGridVue
              style="width: 100%; height: calc(100vh - 380px)"
              class="ag-theme-alpine"
              id="chargesGrid"
              :columnDefs="chargeHeaders"
              :context="context"
              @grid-ready="onChargesGridReady"
              :defaultColDef="defaultColDef"
              :grid-options="chargesGridOptions"
              :rowData="filteredCharges"
            ></AgGridVue>
          </v-tab-item>

          <!-- Logs tab -->
          <v-tab-item>
            <v-row class="ma-0">
              <v-col cols="12">
                <span
                  class="py-12"
                  v-if="subHireJobLogs && subHireJobLogs.length == 0"
                >
                  No logs to show!
                </span>

                <v-timeline dense clipped v-else>
                  <v-timeline-item
                    v-for="(log, i) in subHireJobLogs"
                    :key="i"
                    icon-color="primary"
                    small
                  >
                    <v-card class="elevation-2">
                      <v-card-text>
                        <table class="Full-Width">
                          <tbody>
                            <tr>
                              <th class="text-left">{{ log.updated_by }}</th>
                              <td class="text-caption text-right">
                                {{ log.created | formatDate(true) }}
                              </td>
                            </tr>
                            <tr>
                              <td colspan="2">{{ log.message }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <AddLegDialog
      v-model="showAddLegDialog"
      :legRow="firstLegId"
      :info="addLegInfo"
      :excludeLegIds="selectedLegIds"
      :baseRevenue="Number(totalRevenue)"
      :baseAdditionalCharge="Number(totalAdditionalCharge)"
      :baseTotalRevenue="Number(totalRevenueWithAdditionalCharge)"
      :baseTaxableAmount="Number(totalTaxableAmount)"
      :baseTotalRevenueWithTax="Number(totalRevenueWithTax)"
      :baseLegCount="selectedLegs.length"
      :baseTotalCost="Number(subHireFormDetails.total_cost || 0)"
      :baseTotalCostWithTax="
        Number(subHireFormDetails.total_cost_with_tax || 0)
      "
      :transporterRate="Number(subHireFormDetails.rate || 0)"
      :transporterTaxPercentage="Number(subHireFormDetails.tax_percentage || 0)"
      :chargesTotal="chargesRateTotal"
      @confirm="onLegsConfirmed"
    />

    <EditChargesDialog
      v-model="showEditChargesDialog"
      :subHireFormDetails="subHireFormDetails"
      :legCount="selectedLegs.length"
      :totalRevenueWithAdditionalCharge="
        Number(totalRevenueWithAdditionalCharge)
      "
      :readonly="!canEditCharges"
      :laneServiceType="laneServiceType"
      @save="onChargesSaved"
    />

    <ManageChargesDialog
      v-model="showManageChargesDialog"
      :charges="subHireFormDetails.sub_hire_job_charges"
      :readonly="!canEditCharges"
      @save="onChargeListSaved"
    />

    <NewConfirmationDialog
      v-model="showRemoveLegConfirm"
      icon="mdi-alert-circle-outline"
      status="error"
      title="Remove Leg"
      text="Are you sure you want to remove this leg?"
      confirmButton="Remove"
      rejectButton="Cancel"
      @confirmed="removeLeg"
    />

    <NewConfirmationDialog
      v-model="showMoveToPendingConfirm"
      icon="mdi-help-circle"
      status="primary"
      title="Move To Pending"
      text="Are you sure you want to move this sub hire to Pending?"
      confirmButton="Move To Pending"
      rejectButton="Cancel"
      @confirmed="moveToPending"
    />
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import AddLegDialog from "@/components/ModuleBased/dialogForms/AddLegDialog.vue";
import EditChargesDialog from "@/components/ModuleBased/dialogForms/EditChargesDialog.vue";
import ManageChargesDialog from "@/components/ModuleBased/dialogForms/ManageChargesDialog.vue";
import NewConfirmationDialog from "@/components/General/NewConfirmationDialog.vue";
import Pagination from "@/components/General/Pagination.vue";
import StatusChip from "@/components/AgGridComponents/General/StatusChip.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
  getColorWithLighten,
  getColorWithDarken,
} from "@/utils/functions";

export default {
  components: {
    AgGridVue,
    AddLegDialog,
    EditChargesDialog,
    ManageChargesDialog,
    NewConfirmationDialog,
    Pagination,
    StatusChip,
  },

  data() {
    return {
      permissions: null,
      activeTab: 0,
      formErrors: {},
      getColorWithLighten,
      getColorWithDarken,

      showAddLegDialog: false,
      showEditChargesDialog: false,
      showManageChargesDialog: false,
      showRemoveLegConfirm: false,
      showMoveToPendingConfirm: false,
      pendingRemoveLeg: null,
      // Explicit ids the user clicked the red remove button on - the one
      // source of truth the backend uses to unlink a leg. Cleared after every
      // save so a leg is never removed twice, and never inferred just because
      // it's absent from selectedLegs.
      removedLegIds: [],
      selectedLegs: [],
      displayedLegs: [],
      legSearch: "",
      legSearchTimer: null,
      legPageNo: 1,
      legItemsPerPage: 50,
      legTotalItems: 0,
      chargeSearch: "",
      subHireJobLogs: [],
      logsLoaded: false,

      laneServiceType: null,

      subHireFormDetails: {
        cargo_liability_insurance: "No",
        general_third_party_insurance: "No",
        sub_hire_job_charges: [],
      },

      selectedLegHeaders: [
        { headerName: "Leg", field: "leg_id", minWidth: 150 },
        { headerName: "DA", field: "da_no", minWidth: 120 },
        { headerName: "BOE", field: "boe", minWidth: 120 },
        {
          headerName: "Container No",
          field: "sub_hire_leg_containers",
          minWidth: 120,
        },
        { headerName: "Sub Hire No.", field: "sub_hire_no", minWidth: 120 },
        { headerName: "Rate (AED)", field: "leg_rate", minWidth: 120 },
        {
          headerName: "Status",
          field: "status",
          minWidth: 140,
          cellRenderer: "StatusChip",
          cellRendererParams: (params) => ({ status: params.value }),
        },
      ],

      chargeHeaders: [
        { headerName: "Charge", field: "charge_title", minWidth: 150 },
        { headerName: "Description", field: "description", minWidth: 180 },
        { headerName: "Cost GL No.", field: "cost_gl_no", minWidth: 120 },
        { headerName: "Tax (%)", field: "tax_percentage", minWidth: 90 },
        { headerName: "Rate (AED)", field: "rate", minWidth: 120 },
      ],

      defaultColDef: { resizable: true, flex: 1 },
      selectedLegGridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
      chargesGridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },

      backRoute: { path: "/app/admin/sub-hire/pending" },
      gridApi: null,
      gridColumnApi: null,
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    canEditLegs() {
      return ["Created", "Pending"].includes(this.subHireFormDetails.status);
    },
    canEditCharges() {
      return ["Created", "Pending", "Approved"].includes(
        this.subHireFormDetails.status,
      );
    },
    firstLegId() {
      return (
        this.subHireFormDetails.first_leg_id ||
        (this.selectedLegs[0] && this.selectedLegs[0].id)
      );
    },
    addLegInfo() {
      const d = this.subHireFormDetails || {};
      return {
        customer: d.customer,
        transporter: d.transporter_company_name || d.transporter,
        route: d.leg_lane_title,
        containerType: d.da_container_type,
        movementDate:
          (this.selectedLegs[0] && this.selectedLegs[0].movement_date) || null,
      };
    },
    selectedLegIds() {
      return this.selectedLegs.map((leg) => leg.id);
    },
    filteredCharges() {
      const charges = this.subHireFormDetails.sub_hire_job_charges || [];
      if (!this.chargeSearch) return charges;
      const term = this.chargeSearch.toLowerCase();
      return charges.filter((charge) =>
        [charge.charge_title, charge.description, charge.cost_gl_no]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term)),
      );
    },
    totalRevenue() {
      let total = 0;
      this.selectedLegs.forEach((leg) => {
        total += parseFloat(leg.leg_rate || 0);
      });
      return total.toFixed(2);
    },
    totalAdditionalCharge() {
      let total = 0;
      this.selectedLegs.forEach((leg) => {
        total += parseFloat(leg.customer_da_total_additional_charge || 0);
      });
      return total.toFixed(2);
    },
    totalRevenueWithAdditionalCharge() {
      return (
        parseFloat(this.totalRevenue) + parseFloat(this.totalAdditionalCharge)
      ).toFixed(2);
    },
    totalTaxableAmount() {
      let total = 0;
      this.selectedLegs.forEach((leg) => {
        total += parseFloat(leg.customer_da_total_taxable_amount || 0);
      });
      return total.toFixed(2);
    },
    totalRevenueWithTax() {
      return (
        parseFloat(this.totalRevenueWithAdditionalCharge) +
        parseFloat(this.totalTaxableAmount)
      ).toFixed(2);
    },
    marginRate() {
      return (
        parseFloat(this.totalRevenueWithAdditionalCharge) -
        parseFloat(this.subHireFormDetails.total_cost || 0)
      ).toFixed(2);
    },
    marginPercent() {
      if (!parseFloat(this.totalRevenueWithAdditionalCharge)) return "0.00";
      return (
        (parseFloat(this.marginRate) /
          parseFloat(this.totalRevenueWithAdditionalCharge)) *
        100
      ).toFixed(2);
    },
    transporterTitle() {
      return (
        this.subHireFormDetails.transporter_company_name ||
        this.subHireFormDetails.transporter
      );
    },
    chargesRateTotal() {
      let total = 0;
      (this.subHireFormDetails.sub_hire_job_charges || []).forEach((charge) => {
        if (charge.rate) {
          total += parseFloat(parseFloat(charge.rate).toFixed(2));
        }
      });
      return total;
    },
    movementCompletionLabel() {
      const total = this.selectedLegs.length;
      if (!total) return "N/A";
      const completed = this.selectedLegs.filter(
        (leg) => leg.status === "Completed"
      ).length;
      return `${completed} of ${total} legs completed`;
    },
    summaryFields() {
      const currency = this.$globals.currency;
      const orNA = (val) =>
        val === null || val === undefined || val === "" ? "N/A" : val;
      return [
        {
          label: "Sub Hire Status",
          value: this.subHireFormDetails.status,
          chip: true,
        },
        { label: "Route", value: orNA(this.subHireFormDetails.leg_lane_title) },
        { label: "Customer", value: orNA(this.subHireFormDetails.customer) },
        { label: "Transporter", value: orNA(this.transporterTitle) },
        {
          label: "Transport Charge",
          value: `${this.subHireFormDetails.rate || 0} ${currency}`,
        },
        {
          label: "Tax Percentage (Cost)",
          value: `${Number(this.subHireFormDetails.tax_percentage || 0).toFixed(2)}%`,
        },
        {
          label: "DA Container Type",
          value: orNA(this.subHireFormDetails.da_container_type),
        },
        { label: "Sub Hire Leg Count", value: this.selectedLegs.length },
        {
          label: "Movement Completed",
          value: this.movementCompletionLabel,
        },
        { label: "Revenue", value: `${this.totalRevenue} ${currency}` },
        {
          label: "Additional Charge",
          value: `${this.totalAdditionalCharge} ${currency}`,
        },
        {
          label: "Total Revenue",
          value: `${this.totalRevenueWithAdditionalCharge} ${currency}`,
        },
        {
          label: "Taxable Amount",
          value: `${this.totalTaxableAmount} ${currency}`,
        },
        {
          label: "Total Revenue With Tax",
          value: `${this.totalRevenueWithTax} ${currency}`,
        },
        {
          label: "Total Cost",
          value: `${this.subHireFormDetails.total_cost || 0} ${currency}`,
        },
        {
          label: "Total Cost With Tax",
          value: `${
            this.subHireFormDetails.total_cost_with_tax || 0
          } ${currency}`,
        },
        { label: "Margin Rate", value: `${this.marginRate} ${currency}` },
        { label: "Margin Percentage", value: `${this.marginPercent}%` },
        {
          label: "Reason For Sub Hire",
          value: orNA(this.subHireFormDetails.reason_for_sub_hire),
        },
      ];
    },
  },

  watch: {
    activeTab(val) {
      if (val === 2 && !this.logsLoaded) {
        this.getSubHireJobLogs();
      }
    },
    legSearch() {
      clearTimeout(this.legSearchTimer);
      this.legSearchTimer = setTimeout(() => {
        this.legPageNo = 1;
        this.getDisplayedLegs();
      }, 400);
    },
  },

  methods: {
    subHireStatusColorMap(status) {
      const map = {
        Created: "grey",
        Pending: "primary",
        Approved: "blue",
        "Invoice Updated": "purple",
        Completed: "success",
        Cancelled: "error",
      };
      return map[status] || null;
    },

    subHireStatusLighten(status) {
      const color = this.subHireStatusColorMap(status);
      return color ? `${color} lighten-4` : this.getColorWithLighten(status);
    },

    subHireStatusDarken(status) {
      const color = this.subHireStatusColorMap(status);
      return color ? `${color} darken-4` : this.getColorWithDarken(status);
    },

    callBackRoute() {
      this.$router.push(this.backRoute);
    },

    setBackRoute() {
      if ("backRoute" in this.$route.query) {
        this.backRoute = { path: this.$route.query.backRoute };
      }
    },

    legItemsPerPageChanged(e) {
      this.legPageNo = 1;
      this.legItemsPerPage = e;
      this.getDisplayedLegs();
    },

    legPrevPage() {
      this.legPageNo--;
      this.getDisplayedLegs();
    },

    legNextPage() {
      this.legPageNo++;
      this.getDisplayedLegs();
    },

    getDisplayedLegs() {
      this.$bus.$emit("showLoader", true);
      const params = {
        limit: this.legItemsPerPage,
        offset: (this.legPageNo - 1) * this.legItemsPerPage,
      };
      if (this.legSearch) params.search = this.legSearch;
      this.$api.subHire
        .getSubHireLegs(this.$route.params.id, params)
        .then((res) => {
          this.displayedLegs = res.data || [];
          this.legTotalItems =
            res.count != null ? res.count : this.displayedLegs.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getSubHireJobLogs() {
      this.$bus.$emit("showLoader", true);
      this.$api.subHire
        .getSubHireJObLogs(this.$route.params.id)
        .then((res) => {
          this.subHireJobLogs = res.data;
          this.logsLoaded = true;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    resizeGrid() {
      if (this.selectedLegGridOptions && "api" in this.selectedLegGridOptions) {
        setTimeout(() => {
          this.selectedLegGridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },

    onSelectedLegGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
      this.setupRemoveColumn();
    },

    onChargesGridReady(params) {
      params.api.sizeColumnsToFit();
    },

    setupRemoveColumn() {
      const hasRemoveCol = this.selectedLegHeaders.some(
        (h) => h.field === "remove",
      );
      if (this.canEditLegs && !hasRemoveCol) {
        this.selectedLegHeaders.push({
          headerName: "",
          field: "remove",
          minWidth: 90,
          maxWidth: 90,
          cellRenderer: () => {
            if (!this.subHireFormDetails.transporter) {
              return `<span style="color:#bdbdbd;cursor:not-allowed" data-action="remove-disabled">
                <i class="mdi mdi-delete" style="font-size:18px"></i>
              </span>`;
            }
            return `<span style="color:#f44336;cursor:pointer" data-action="remove">
              <i class="mdi mdi-delete" style="font-size:18px"></i>
            </span>`;
          },
          onCellClicked: (params) => {
            if (!this.subHireFormDetails.transporter) return;
            const target = params.event.target.closest("[data-action]");
            if (!target) return;
            this.confirmRemoveLeg(params.data);
          },
        });
      }
    },

    onLegsConfirmed(legs) {
      legs.forEach((leg) => {
        if (!this.selectedLegs.find((l) => l.id === leg.id)) {
          this.selectedLegs.push(leg);
        }
      });
      this.setAdditionalChargeDetails();
      this.saveSubHire("Leg(s) added successfully");
    },

    confirmRemoveLeg(leg) {
      this.pendingRemoveLeg = leg;
      this.showRemoveLegConfirm = true;
    },

    removeLeg() {
      if (!this.pendingRemoveLeg) return;
      this.selectedLegs = this.selectedLegs.filter(
        (l) => l.id !== this.pendingRemoveLeg.id,
      );
      if (!this.removedLegIds.includes(this.pendingRemoveLeg.id)) {
        this.removedLegIds.push(this.pendingRemoveLeg.id);
      }
      this.pendingRemoveLeg = null;
      this.setAdditionalChargeDetails();
      this.saveSubHire("Leg removed successfully");
    },

    setAdditionalChargeDetails() {
      let transportTotalAdditionalCharge = 0.0;
      (this.subHireFormDetails.sub_hire_job_charges || []).forEach((charge) => {
        if (charge.rate) {
          transportTotalAdditionalCharge += parseFloat(
            parseFloat(charge.rate).toFixed(2),
          );
        }
      });

      const legCount = this.selectedLegs.length;
      this.subHireFormDetails.total_cost = (
        (parseFloat(this.subHireFormDetails.rate || 0) +
          transportTotalAdditionalCharge) *
        legCount
      ).toFixed(2);

      this.setTotalTaxableAmount();
    },

    setTotalTaxableAmount() {
      let totalTaxable = 0.0;
      (this.subHireFormDetails.sub_hire_job_charges || []).forEach((charge) => {
        if (charge.rate) {
          totalTaxable +=
            (parseFloat(charge.rate) * parseFloat(charge.tax_percentage || 0)) /
            100;
        }
      });
      if (this.subHireFormDetails.rate) {
        totalTaxable +=
          (parseFloat(this.subHireFormDetails.rate) *
            parseFloat(this.subHireFormDetails.tax_percentage || 0)) /
          100;
        totalTaxable = totalTaxable * this.selectedLegs.length;
      }
      this.subHireFormDetails.total_taxable_amount = totalTaxable.toFixed(2);
      this.subHireFormDetails.total_cost_with_tax = (
        parseFloat(this.subHireFormDetails.total_cost || 0) +
        parseFloat(this.subHireFormDetails.total_taxable_amount)
      ).toFixed(2);
    },

    onChargesSaved(updatedDetails) {
      this.subHireFormDetails = updatedDetails;
      this.setAdditionalChargeDetails();
      this.saveSubHire("Charges updated successfully");
    },

    onChargeListSaved(updatedCharges) {
      this.subHireFormDetails.sub_hire_job_charges = updatedCharges;
      this.setAdditionalChargeDetails();
      this.saveSubHire("Charges updated successfully");
    },

    getSubHireObject(id) {
      this.$bus.$emit("showLoader", true);
      this.$api.subHire
        .getSubHireObject({ id })
        .then((res) => {
          this.subHireFormDetails = res.data;
          this.subHireFormDetails.sub_hire_job_charges =
            res.data.sub_hire_job_charges || [];
          this.laneServiceType = res.data.da_lane_service_type;
          this.setupRemoveColumn();
          this.getSelectedLegsData();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getSelectedLegsData() {
      this.$bus.$emit("showLoader", true);
      this.$api.subHire
        .getSubHireLegs(this.$route.params.id, { limit: "all" })
        .then((res) => {
          this.selectedLegs = res.data || [];
          this.setAdditionalChargeDetails();
          this.legPageNo = 1;
          this.legTotalItems = this.selectedLegs.length;
          this.displayedLegs = this.selectedLegs.slice(0, this.legItemsPerPage);
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    moveToPending() {
      this.saveSubHire("Sub hire moved to Pending", "Pending");
    },

    saveSubHire(successMessage, statusOverride) {
      this.formErrors = {};
      this.$bus.$emit("showLoader", true);
      const data = {
        current_leg: this.firstLegId,
        ...this.subHireFormDetails,
        selected_legs: this.selectedLegIds,
        // Explicit removal ids from the red remove button - the backend only
        // unlinks legs listed here, never legs merely absent from
        // selected_legs (which a stale grid/search state could truncate).
        removed_leg_ids: this.removedLegIds,
      };
      if (statusOverride) {
        data.status = statusOverride;
      }
      this.$api.subHire
        .updateSubHireJob({ id: this.$route.params.id, data })
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: successMessage || "Success",
            color: "success",
          });
          this.removedLegIds = [];
          this.getSubHireObject(this.$route.params.id);
          this.getSubHireJobLogs();
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't update sub hire!",
            color: "error",
          });
          if (err.data) this.formErrors = err.data;
        });
    },
  },

  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      this.permissions = getPermissions("subhire_pending");
      if (!this.permissions || !this.permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`,
        );
      } else {
        this.setBackRoute();
        this.getSubHireObject(this.$route.params.id);
      }
    }
  },
};
</script>
